import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { 
    FaPlus, FaEdit, FaTrash, FaSpinner, FaEye, FaTasks, FaCheck,
    FaSitemap, FaFileSignature, FaUser, FaClipboardCheck, FaChartPie,
    FaHeartbeat, FaMedkit, FaUsers, FaUpload, FaCheckCircle, FaTimesCircle
} from 'react-icons/fa';
import './SaudeBeneficios.css'; // Importa o CSS novo
import { useAuth } from '../../hooks/useAuth'; // Para pegar o perfil
import { useDropzone } from 'react-dropzone';

// Reutilizando o Modal de T&D/AD
const Modal = ({ show, onClose, title, children, footer, size = 'default' }) => {
    if (!show) return null;
    const modalStyle = size === 'large' ? { maxWidth: '900px', width: '90%' } : { maxWidth: '700px', width: '90%' };
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()} style={modalStyle}>
                <div className="modal-header">
                    <h2>{title}</h2>
                    <button onClick={onClose} className="close-button">&times;</button>
                </div>
                <div className="modal-body">{children}</div>
                {footer && <div className="modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

const LoadingSpinner = () => (
    <div className="loading-spinner">
        <FaSpinner className="fa-spin" size={30} />
        <span style={{ marginLeft: '10px' }}>Carregando...</span>
    </div>
);

// =================================================================
// === [ADMIN] ABA 1: VISÃO GERAL (Atestados Pendentes) ===========
// =================================================================
const AdminVisaoGeral = ({ onSelectUser }) => {
    const [pendentes, setPendentes] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchPendentes = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/api/rh/atestados/pendentes');
            setPendentes(data);
        } catch (error) {
            toast.error("Falha ao buscar atestados pendentes.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPendentes();
    }, [fetchPendentes]);

    const handleAnalisar = (atestado) => {
        // TODO: Abrir modal de análise
        toast.info(`Analisar atestado de ${atestado.colaborador_nome}`);
        // Para uma melhor UX, poderíamos ter um modal que chama a rota /api/rh/atestados/:id/analisar
    };

    if (loading) return <LoadingSpinner />;

    return (
        <>
            <div className="sb-section">
                <div className="sb-section-header">
                    <h2>Atestados Pendentes de Análise</h2>
                </div>
                {pendentes.length === 0 ? (
                    <p>Nenhum atestado pendente.</p>
                ) : (
                    <div className="atestado-list">
                        {pendentes.map(atestado => (
                            <div key={atestado.id} className="atestado-item">
                                <div className="atestado-info">
                                    <img src={atestado.foto_perfil || '/avatar-padrao.png'} alt="Foto" />
                                    <div>
                                        <h5>{atestado.colaborador_nome}</h5>
                                        <span>Início: {new Date(atestado.data_inicio).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</span>
                                    </div>
                                </div>
                                <span className="atestado-dias">{atestado.dias_afastado} dia(s)</span>
                                <div>
                                    <button className="sb-button-secondary" onClick={() => handleAnalisar(atestado)}>
                                        Analisar
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            <div className="sb-section">
                <div className="sb-section-header">
                    <h2>Saúde Ocupacional (ASO)</h2>
                </div>
                <p>Em breve: Dashboard com exames periódicos vencendo nos próximos 30 dias.</p>
            </div>
        </>
    );
};

// =================================================================
// === [ADMIN] ABA 2: GESTÃO DE COLABORADORES ======================
// =================================================================
const AdminGestaoColaboradores = ({ colaboradores, catalogo }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [beneficios, setBeneficios] = useState([]);
    const [asos, setAsos] = useState([]);
    
    // --- States dos Modais ---
    const [modalBeneficioOpen, setModalBeneficioOpen] = useState(false);
    const [novoBeneficio, setNovoBeneficio] = useState({
        beneficio_id: '',
        status: 'ativo',
        data_adesao: new Date().toISOString().split('T')[0],
        valor_colaborador: 0,
        valor_empresa: 0,
        detalhes_plano: ''
    });

    const [modalAsoOpen, setModalAsoOpen] = useState(false);
    const [novoAso, setNovoAso] = useState({
        tipo_exame: 'periodico',
        data_exame: new Date().toISOString().split('T')[0],
        data_vencimento: '',
        resultado: 'apto'
    });
    const [asoFile, setAsoFile] = useState(null);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: acceptedFiles => setAsoFile(acceptedFiles[0]),
        accept: 'image/jpeg, image/png, application/pdf',
        maxFiles: 1
    });
    
    // --- Funções de Fetch (Separadas) ---
    const fetchBeneficios = useCallback(async (userId) => {
        try {
            const { data } = await api.get(`/api/rh/beneficios/colaborador/${userId}`);
            setBeneficios(data);
        } catch (error) {
            toast.error("Falha ao buscar benefícios.");
        }
    }, []);

    const fetchAsos = useCallback(async (userId) => {
        try {
            const { data } = await api.get(`/api/rh/saude-ocupacional/${userId}`);
            setAsos(data);
        } catch (error) {
            toast.error("Falha ao buscar ASOs.");
        }
    }, []);

    const handleUserChange = async (userId) => {
        if (!userId) {
            setSelectedUser(null);
            setBeneficios([]);
            setAsos([]);
            return;
        }
        
        setLoading(true);
        const user = colaboradores.find(c => c.id === parseInt(userId));
        setSelectedUser(user);
        
        await Promise.all([
            fetchBeneficios(userId),
            fetchAsos(userId)
        ]);
        setLoading(false);
    };

    // --- Handlers Modal Benefício ---
    const handleBeneficioChange = (e) => {
        const { name, value } = e.target;
        setNovoBeneficio(prev => ({ ...prev, [name]: value }));
    };

    const handleBeneficioSubmit = async () => {
        if (!novoBeneficio.beneficio_id) {
            toast.warn("Selecione um benefício do catálogo.");
            return;
        }
        try {
            const body = {
                ...novoBeneficio,
                user_id: selectedUser.id
            };
            await api.post('/api/rh/beneficios/colaborador', body);
            toast.success("Benefício adicionado ao colaborador!");
            fetchBeneficios(selectedUser.id);
            setModalBeneficioOpen(false);
        } catch (error) {
            toast.error("Falha ao adicionar benefício.");
        }
    };

    // --- Handlers Modal ASO ---
    const handleAsoChange = (e) => {
        const { name, value } = e.target;
        setNovoAso(prev => ({ ...prev, [name]: value }));
    };
    
    const handleAsoSubmit = async () => {
        const formData = new FormData();
        formData.append('user_id', selectedUser.id);
        formData.append('tipo_exame', novoAso.tipo_exame);
        formData.append('data_exame', novoAso.data_exame);

        // --- CORREÇÃO AQUI ---
        // Só anexa a data de vencimento se ela for preenchida
        if (novoAso.data_vencimento) {
            formData.append('data_vencimento', novoAso.data_vencimento);
        }
        // --- FIM DA CORREÇÃO ---

        formData.append('resultado', novoAso.resultado);
        if (asoFile) {
            formData.append('aso', asoFile);
        }

        try {
            await api.post('/api/rh/saude-ocupacional', formData, {
                 headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("ASO registrado com sucesso!");
            fetchAsos(selectedUser.id);
            setAsoFile(null);
            setModalAsoOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.error || "Falha ao registrar ASO.");
        }
    };

    return (
        <div className="sb-section">
            <div className="sb-form-group">
                <label>Selecione um Colaborador para Gerenciar</label>
                <select onChange={e => handleUserChange(e.target.value)}>
                    <option value="">-- Selecione --</option>
                    {colaboradores.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
            </div>

            {loading && <LoadingSpinner />}
            
            {selectedUser && !loading && (
                <div style={{ marginTop: '2rem' }}>
                    {/* 1. Benefícios */}
                    <div className="sb-section">
                        <div className="sb-section-header">
                            <h2>Benefícios de {selectedUser.nome}</h2>
                            <button className="sb-button-primary" onClick={() => setModalBeneficioOpen(true)}>
                                <FaPlus /> Adicionar Benefício
                            </button>
                        </div>
                        <table className="sb-table">
                            <thead><tr><th>Benefício</th><th>Provedor</th><th>Custo Colab.</th><th>Status</th><th>Ações</th></tr></thead>
                            <tbody>
                                {beneficios.length === 0 && <tr><td colSpan="5">Nenhum benefício.</td></tr>}
                                {beneficios.map(b => (
                                    <tr key={b.id}>
                                        <td>{b.nome_beneficio}</td>
                                        <td>{b.provedor}</td>
                                        <td>R$ {b.valor_colaborador.toFixed(2)}</td>
                                        <td>{b.status}</td>
                                        <td><button className="actions-column edit"><FaEdit /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    
                    {/* 2. Saúde Ocupacional (ASO) */}
                     <div className="sb-section">
                        <div className="sb-section-header">
                            <h2>Saúde Ocupacional (ASO)</h2>
                            <button className="sb-button-primary" onClick={() => setModalAsoOpen(true)}>
                                <FaPlus /> Novo Registro ASO
                            </button>
                        </div>
                         <table className="sb-table">
                            <thead><tr><th>Exame</th><th>Data</th><th>Vencimento</th><th>Resultado</th><th>Ações</th></tr></thead>
                            <tbody>
                                {asos.length === 0 && <tr><td colSpan="5">Nenhum exame.</td></tr>}
                                {asos.map(a => (
                                    <tr key={a.id}>
                                        <td>{a.tipo_exame}</td>
                                        <td>{new Date(a.data_exame).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</td>
                                        <td>{a.data_vencimento ? new Date(a.data_vencimento).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : 'N/A'}</td>
                                        <td>{a.resultado}</td>
                                        <td>
                                            {a.path_documento && <a href={api.defaults.baseURL + a.path_documento} target="_blank" rel="noopener noreferrer"><FaEye /></a>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal de Adicionar Benefício */}
            <Modal
                show={modalBeneficioOpen}
                onClose={() => setModalBeneficioOpen(false)}
                title={`Adicionar Benefício para ${selectedUser?.nome}`}
                footer={
                    <>
                        <button className="sb-button-secondary" onClick={() => setModalBeneficioOpen(false)}>Cancelar</button>
                        <button className="sb-button-primary" onClick={handleBeneficioSubmit}>Salvar</button>
                    </>
                }
            >
                <form className="sb-form">
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Benefício do Catálogo</label>
                        <select name="beneficio_id" value={novoBeneficio.beneficio_id} onChange={handleBeneficioChange}>
                            <option value="">-- Selecione --</option>
                            {catalogo.map(c => <option key={c.id} value={c.id}>{c.nome_beneficio} ({c.provedor})</option>)}
                        </select>
                    </div>
                    <div className="sb-form-group">
                        <label>Data Adesão</label>
                        <input type="date" name="data_adesao" value={novoBeneficio.data_adesao} onChange={handleBeneficioChange} />
                    </div>
                    <div className="sb-form-group">
                        <label>Status</label>
                        <select name="status" value={novoBeneficio.status} onChange={handleBeneficioChange}>
                            <option value="ativo">Ativo</option>
                            <option value="pendente_aprovacao">Pendente</option>
                            <option value="cancelado">Cancelado</option>
                        </select>
                    </div>
                    <div className="sb-form-group">
                        <label>Custo Colaborador (R$)</label>
                        <input type="number" name="valor_colaborador" value={novoBeneficio.valor_colaborador} onChange={handleBeneficioChange} />
                    </div>
                    <div className="sb-form-group">
                        <label>Custo Empresa (R$)</label>
                        <input type="number" name="valor_empresa" value={novoBeneficio.valor_empresa} onChange={handleBeneficioChange} />
                    </div>
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Detalhes (Plano, Matrícula, etc)</label>
                        <input name="detalhes_plano" value={novoBeneficio.detalhes_plano} onChange={handleBeneficioChange} />
                    </div>
                </form>
            </Modal>
            
            {/* Modal de Novo ASO */}
            <Modal
                show={modalAsoOpen}
                onClose={() => setModalAsoOpen(false)}
                title={`Novo ASO para ${selectedUser?.nome}`}
                 footer={
                    <>
                        <button className="sb-button-secondary" onClick={() => setModalAsoOpen(false)}>Cancelar</button>
                        <button className="sb-button-primary" onClick={handleAsoSubmit}>Salvar Registro</button>
                    </>
                }
            >
                <form className="sb-form">
                    <div className="sb-form-group">
                        <label>Tipo de Exame</label>
                        <select name="tipo_exame" value={novoAso.tipo_exame} onChange={handleAsoChange}>
                            <option value="admissional">Admissional</option>
                            <option value="periodico">Periódico</option>
                            <option value="demissional">Demissional</option>
                            <option value="mudanca_funcao">Mudança de Função</option>
                            <option value="retorno_trabalho">Retorno ao Trabalho</option>
                        </select>
                    </div>
                    <div className="sb-form-group">
                        <label>Resultado</label>
                        <select name="resultado" value={novoAso.resultado} onChange={handleAsoChange}>
                            <option value="apto">Apto</option>
                            <option value="inapto">Inapto</option>
                        </select>
                    </div>
                    <div className="sb-form-group">
                        <label>Data do Exame</label>
                        <input type="date" name="data_exame" value={novoAso.data_exame} onChange={handleAsoChange} />
                    </div>
                    <div className="sb-form-group">
                        <label>Data de Vencimento (Opcional)</label>
                        <input type="date" name="data_vencimento" value={novoAso.data_vencimento} onChange={handleAsoChange} />
                    </div>
                     <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Anexar Documento ASO (Opcional)</label>
                        <div {...getRootProps({ className: 'upload-box' })}>
                            <input {...getInputProps()} />
                            {asoFile ? (
                                <p style={{ color: '#101C5D', fontWeight: 600 }}>Arquivo: {asoFile.name}</p>
                            ) : isDragActive ? (
                                <p>Solte o arquivo aqui ...</p>
                            ) : (
                                <p>Arraste e solte o arquivo, ou clique para selecionar</p>
                            )}
                        </div>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

// =================================================================
// === [ADMIN] ABA 3: CATÁLOGO DE BENEFÍCIOS =======================
// =================================================================
const AdminCatalogo = ({ catalogo, loading, fetchCatalogo }) => {
    
    // --- LÓGICA DO MODAL (ADICIONADA) ---
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [beneficioAtual, setBeneficioAtual] = useState(null);

    const initialState = {
        nome_beneficio: '',
        categoria: 'Saude',
        descricao: '',
        provedor: '',
        status: 'ativo'
    };

    const handleOpenModal = (beneficio = null) => {
        if (beneficio) {
            setIsEditMode(true);
            setBeneficioAtual(beneficio);
        } else {
            setIsEditMode(false);
            setBeneficioAtual(initialState);
        }
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setBeneficioAtual(null);
        setIsEditMode(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setBeneficioAtual(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        try {
            if (isEditMode) {
                await api.put(`/api/rh/beneficios/catalogo/${beneficioAtual.id}`, beneficioAtual);
                toast.success("Benefício atualizado!");
            } else {
                await api.post('/api/rh/beneficios/catalogo', beneficioAtual);
                toast.success("Benefício criado no catálogo!");
            }
            fetchCatalogo(); // Atualiza a lista
            handleCloseModal();
        } catch (error) {
            toast.error(`Falha ao ${isEditMode ? 'atualizar' : 'criar'} benefício.`);
            console.error(error);
        }
    };
    // --- FIM DA LÓGICA DO MODAL ---

    if (loading) return <LoadingSpinner />;
    
    return (
         <div className="sb-section">
            <div className="sb-section-header">
                <h2>Catálogo de Benefícios da Empresa</h2>
                {/* Botão agora funcional */}
                <button className="sb-button-primary" onClick={() => handleOpenModal(null)}>
                    <FaPlus /> Novo Benefício
                </button>
            </div>
            <table className="sb-table">
                <thead><tr><th>Nome</th><th>Categoria</th><th>Provedor</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                    {catalogo.map(c => (
                        <tr key={c.id}>
                            <td>{c.nome_beneficio}</td>
                            <td>{c.categoria}</td>
                            <td>{c.provedor}</td>
                            <td>{c.status}</td>
                            <td className="actions-column">
                                <button className="edit" onClick={() => handleOpenModal(c)}>
                                    <FaEdit />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Modal de Criar/Editar Benefício (ADICIONADO) */}
            <Modal
                show={modalOpen}
                onClose={handleCloseModal}
                title={isEditMode ? "Editar Benefício" : "Novo Benefício"}
                footer={
                    <>
                        <button className="sb-button-secondary" onClick={handleCloseModal}>Cancelar</button>
                        <button className="sb-button-primary" onClick={handleSubmit}>Salvar</button>
                    </>
                }
            >
                <form className="sb-form">
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Nome do Benefício</label>
                        <input name="nome_beneficio" value={beneficioAtual?.nome_beneficio || ''} onChange={handleChange} />
                    </div>
                    <div className="sb-form-group">
                        <label>Categoria</label>
                        <select name="categoria" value={beneficioAtual?.categoria || 'Saude'} onChange={handleChange}>
                            <option value="Saude">Saúde (Plano, Odonto)</option>
                            <option value="Alimentacao">Alimentação (VR, VA)</option>
                            <option value="Transporte">Transporte (VT, Combustível)</option>
                            <option value="Bem-Estar">Bem-Estar (Gympass, etc)</option>
                            <option value="Outro">Outro</option>
                        </select>
                    </div>
                    <div className="sb-form-group">
                        <label>Provedor (Amil, Sodexo, etc)</label>
                        <input name="provedor" value={beneficioAtual?.provedor || ''} onChange={handleChange} />
                    </div>
                    <div className="sb-form-group">
                        <label>Status</label>
                        <select name="status" value={beneficioAtual?.status || 'ativo'} onChange={handleChange}>
                            <option value="ativo">Ativo</option>
                            <option value="inativo">Inativo</option>
                        </select>
                    </div>
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Descrição (Opcional)</label>
                        <textarea name="descricao" value={beneficioAtual?.descricao || ''} onChange={handleChange}></textarea>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

// =================================================================
// === [COLABORADOR] ABA 1: MEUS BENEFÍCIOS ========================
// =================================================================
const ColaboradorBeneficios = ({ user, loading }) => {
    const [beneficios, setBeneficios] = useState([]);
    
    useEffect(() => {
        if (!user) return;
        const fetchMeusBeneficios = async () => {
            try {
                const { data } = await api.get(`/api/rh/beneficios/colaborador/${user.id}`);
                setBeneficios(data);
            } catch (error) {
                toast.error("Falha ao buscar seus benefícios.");
            }
        };
        fetchMeusBeneficios();
    }, [user]);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="sb-section">
            <div className="sb-section-header">
                <h2>Meus Benefícios Ativos</h2>
            </div>
            <div className="beneficio-grid">
                {beneficios.filter(b => b.status === 'ativo').length === 0 && <p>Você não possui benefícios ativos.</p>}
                
                {beneficios.filter(b => b.status === 'ativo').map(b => (
                    <div key={b.id} className="beneficio-card">
                        <div className="beneficio-card-header">
                            <h3>{b.nome_beneficio}</h3>
                            <span>Provedor: {b.provedor || 'N/A'}</span>
                        </div>
                        <div className="beneficio-card-body">
                            <p><span className="detalhe">Plano/Detalhes:</span> {b.detalhes_plano || 'Não informado'}</p>
                            <p><span className="detalhe">Adesão:</span> {b.data_adesao ? new Date(b.data_adesao).toLocaleDateString('pt-BR', {timeZone: 'UTC'}) : 'N/A'}</p>
                            <p className="custo">
                                Custo Mensal Colaborador: R$ {b.valor_colaborador.toFixed(2)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
            
            <div className="sb-section" style={{ marginTop: '2rem' }}>
                <div className="sb-section-header">
                    <h2>Meus Dependentes</h2>
                     <button className="sb-button-secondary"><FaPlus /> Solicitar Inclusão</button>
                </div>
                {/* (Aqui entraria a lista de dependentes da API) */}
                <p>Em breve: Lista de dependentes cadastrados.</p>
            </div>
        </div>
    );
};

// =================================================================
// === [COLABORADOR] ABA 2: MEUS ATESTADOS E SAÚDE =================
// =================================================================
const ColaboradorSaude = ({ user, loading }) => {
    const [atestados, setAtestados] = useState([]);
    const [asos, setAsos] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [atestadoFile, setAtestadoFile] = useState(null);
    const [atestadoForm, setAtestadoForm] = useState({ data_inicio: '', dias_afastado: 1, cid: '', motivo: '' });
    
    const fetchAtestados = useCallback(async () => {
        try {
            const [atestadosRes, asosRes] = await Promise.all([
                 api.get('/api/rh/atestados'), // Rota já busca o do user logado
                 api.get(`/api/rh/saude-ocupacional/${user.id}`) // Rota de admin, mas liberada para o próprio
            ]);
            setAtestados(atestadosRes.data);
            setAsos(asosRes.data);
        } catch (error) { toast.error("Falha ao buscar seu histórico de saúde."); }
    }, [user]);

    useEffect(() => { 
        if(user?.id) {
            fetchAtestados(); 
        }
    }, [user, fetchAtestados]);
    
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: acceptedFiles => setAtestadoFile(acceptedFiles[0]),
        accept: 'image/jpeg, image/png, application/pdf',
        maxFiles: 1
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setAtestadoForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async () => {
        if (!atestadoFile) {
            toast.warn("Por favor, anexe o arquivo do atestado."); return;
        }
        
        const formData = new FormData();
        formData.append('atestado', atestadoFile);
        formData.append('data_inicio', atestadoForm.data_inicio);
        formData.append('dias_afastado', atestadoForm.dias_afastado);
        formData.append('cid', atestadoForm.cid);
        formData.append('motivo', atestadoForm.motivo);

        try {
            await api.post('/api/rh/atestados', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Atestado enviado com sucesso para análise!");
            setModalOpen(false);
            setAtestadoFile(null);
            fetchAtestados(); // Atualiza a lista
        } catch (error) {
            toast.error(error.response?.data?.error || "Falha ao enviar atestado.");
        }
    };
    
    return (
        <>
            <div className="sb-section">
                <div className="sb-section-header">
                    <h2>Enviar Atestado Médico</h2>
                    <button className="sb-button-primary" onClick={() => setModalOpen(true)}>
                        <FaUpload /> Enviar Novo Atestado
                    </button>
                </div>
                <table className="sb-table">
                    <thead><tr><th>Início</th><th>Dias</th><th>Status</th><th>Analisado Por</th><th>Obs. RH</th></tr></thead>
                    <tbody>
                        {atestados.length === 0 && <tr><td colSpan="5">Nenhum atestado.</td></tr>}
                        {atestados.map(a => (
                            <tr key={a.id}>
                                <td>{new Date(a.data_inicio).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</td>
                                <td>{a.dias_afastado}</td>
                                <td>{a.status}</td>
                                <td>{a.analisado_por_nome || 'N/A'}</td>
                                <td>{a.observacao_rh}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal show={modalOpen} onClose={() => setModalOpen(false)} title="Enviar Novo Atestado"
                footer={
                    <>
                        <button className="sb-button-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                        <button className="sb-button-primary" onClick={handleSubmit}>Enviar para Análise</button>
                    </>
                }>
                <div className="sb-form">
                    <div className="sb-form-group">
                        <label>Data Início do Afastamento</label>
                        <input type="date" name="data_inicio" value={atestadoForm.data_inicio} onChange={handleChange} />
                    </div>
                     <div className="sb-form-group">
                        <label>Dias de Afastamento</label>
                        <input type="number" name="dias_afastado" value={atestadoForm.dias_afastado} onChange={handleChange} min="1" />
                    </div>
                    <div className="sb-form-group">
                        <label>CID (Opcional)</label>
                        <input name="cid" value={atestadoForm.cid} onChange={handleChange} />
                    </div>
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Motivo (Opcional)</label>
                        <input name="motivo" value={atestadoForm.motivo} onChange={handleChange} />
                    </div>
                    <div className="sb-form-group" style={{ gridColumn: '1 / -1' }}>
                        <label>Anexar Documento (PDF, JPG, PNG)</label>
                        <div {...getRootProps({ className: 'upload-box' })}>
                            <input {...getInputProps()} />
                            {atestadoFile ? (
                                <p style={{ color: '#101C5D', fontWeight: 600 }}>Arquivo: {atestadoFile.name}</p>
                            ) : isDragActive ? (
                                <p>Solte o arquivo aqui ...</p>
                            ) : (
                                <p>Arraste e solte o arquivo, ou clique para selecionar</p>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

// =================================================================
// === COMPONENTE PRINCIPAL (HUB) ==================================
// =================================================================
const SaudeBeneficios = ({ user }) => {
    const { perfil } = useAuth();
    const isAdminRH = ['admin_geral', 'admin', 'rh'].includes(perfil);
    
    const [activeTab, setActiveTab] = useState(isAdminRH ? 'visaoGeral' : 'meusBeneficios');
    const [loading, setLoading] = useState(true);
    
    // Admin precisa desses dados em várias abas
    const [colaboradores, setColaboradores] = useState([]);
    const [catalogo, setCatalogo] = useState([]);

    const fetchAdminData = useCallback(async () => {
        setLoading(true);
        try {
            const [colabRes, catRes] = await Promise.all([
                api.get('/api/rh/colaboradores'),
                api.get('/api/rh/beneficios/catalogo')
            ]);
            setColaboradores(colabRes.data.filter(c => c.rh_id)); // Só admitidos
            setCatalogo(catRes.data);
        } catch (error) {
            toast.error("Falha ao carregar dados de gestão de benefícios.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isAdminRH) {
            fetchAdminData();
        } else {
            setLoading(false); // Colaborador comum carrega seus próprios dados
        }
    }, [isAdminRH, fetchAdminData]);

    const renderTabContent = () => {
        if (loading && isAdminRH) return <LoadingSpinner />;

        if (isAdminRH) {
            switch (activeTab) {
                case 'visaoGeral':
                    return <AdminVisaoGeral onSelectUser={() => setActiveTab('gestao')} />;
                case 'gestao':
                    return <AdminGestaoColaboradores colaboradores={colaboradores} catalogo={catalogo} />;
                case 'catalogo':
                    return <AdminCatalogo catalogo={catalogo} loading={loading} fetchCatalogo={fetchAdminData} />;
                default:
                    return null;
            }
        } else {
            // Visão do Colaborador
            switch (activeTab) {
                case 'meusBeneficios':
                    return <ColaboradorBeneficios user={user} loading={loading} />;
                case 'meusAtestados':
                    return <ColaboradorSaude user={user} loading={loading} />;
                default:
                    return null;
            }
        }
    };

    return (
        <div className="sb-container">
            <div className="sb-header">
                <h1>Saúde e Benefícios</h1>
            </div>

            {/* Navegação por Abas */}
            <div className="sb-tabs">
                {isAdminRH ? (
                    <>
                        <button 
                            className={`sb-tab-button ${activeTab === 'visaoGeral' ? 'active' : ''}`}
                            onClick={() => setActiveTab('visaoGeral')}
                        >
                            <FaHeartbeat /> Visão Geral
                        </button>
                        <button 
                            className={`sb-tab-button ${activeTab === 'gestao' ? 'active' : ''}`}
                            onClick={() => setActiveTab('gestao')}
                        >
                            <FaUsers /> Gestão de Colaboradores
                        </button>
                        <button 
                            className={`sb-tab-button ${activeTab === 'catalogo' ? 'active' : ''}`}
                            onClick={() => setActiveTab('catalogo')}
                        >
                            <FaSitemap /> Catálogo de Benefícios
                        </button>
                    </>
                ) : (
                    <>
                        <button 
                            className={`sb-tab-button ${activeTab === 'meusBeneficios' ? 'active' : ''}`}
                            onClick={() => setActiveTab('meusBeneficios')}
                        >
                            <FaMedkit /> Meus Benefícios
                        </button>
                        <button 
                            className={`sb-tab-button ${activeTab === 'meusAtestados' ? 'active' : ''}`}
                            onClick={() => setActiveTab('meusAtestados')}
                        >
                            <FaFileSignature /> Meus Atestados e Saúde
                        </button>
                    </>
                )}
            </div>

            {/* Conteúdo da Aba */}
            <div className="sb-tab-content">
                {renderTabContent()}
            </div>
        </div>
    );
};

export default SaudeBeneficios;